import { useEffect, useState } from "react";
import { OrderItem } from "../models/IOrders";
import { getOrderHistory } from "../services/orderHistory";
import { getRestaurantById } from "../services/restaurants";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { useActiveOrder } from "../utils/ActiveOrderContext";
import { useLiveChat } from "../modules/LiveChat";
import { useLanguage } from "../context/LanguageContext";

const toApiDate = (d: Date) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const y = d.getFullYear();
  return `${m}.${day}.${y}`;
};

const formatDisplay = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusColor = (statusName: string) => {
  if (statusName.includes("Teslim")) return "#28a745";
  if (statusName.includes("İptal")) return "#dc3545";
  if (statusName.includes("Hazır")) return "#ffc107";
  if (statusName.includes("Bekl")) return "#17a2b8";
  return "#FF9800";
};

function OrderTrackingPopup() {
  const { activeOrders: contextOrders } = useActiveOrder();
  const { openChat } = useLiveChat();
  const { t, lang } = useLanguage();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [restaurantNames, setRestaurantNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchOrders = () => {
    setLoading(true);
    const yesterday = toApiDate(new Date(Date.now() - 864e5));
    const tomorrow = toApiDate(new Date(Date.now() + 864e5));
    getOrderHistory(yesterday, tomorrow)
      .then((res) => {
        const actives = res.data.filter((o) => o.isOrderEnabled);
        setOrders(actives);
        fetchRestaurantNames(actives);
      })
      .catch(() => setOrders(contextOrders))
      .finally(() => setLoading(false));
  };

  const fetchRestaurantNames = (list: OrderItem[]) => {
    const ids = Array.from(
      new Set(list.map((o) => o.preOrder?.restourantId ?? o.restourantId).filter((id): id is string => !!id))
    );
    if (ids.length === 0) return;
    Promise.all(
      ids.map((id) =>
        getRestaurantById(id)
          .then((res) => ({ id, name: res.data.restourantName }))
          .catch(() => null)
      )
    ).then((results) => {
      const map: Record<string, string> = {};
      results.forEach((r) => {
        if (r) map[r.id] = r.name;
      });
      setRestaurantNames(map);
    });
  };

  useEffect(() => {
    const modalEl = document.getElementById("orderTrackingModal");
    if (!modalEl) return;
    modalEl.addEventListener("show.bs.modal", fetchOrders);
    return () => modalEl.removeEventListener("show.bs.modal", fetchOrders);
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      setOrders((prev) =>
        prev.map((o) => {
          const updated = contextOrders.find((co) => co.id === o.id);
          return updated ? { ...o, orderStatus: updated.orderStatus } : o;
        })
      );
    }
  }, [contextOrders]);

  const getRestaurantId = (order: OrderItem) => order.preOrder?.restourantId ?? order.restourantId ?? "";

  return (
    <div
      className="modal fade"
      id="orderTrackingModal"
      tabIndex={-1}
      aria-labelledby="orderTrackingModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header background-red">
            <h5 className="modal-title text-white" id="orderTrackingModalLabel">
              <i className="bi bi-truck me-2"></i>
              {t("activeOrder.trackingTitle")}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            />
          </div>

          <div className="modal-body p-0">
            {loading ? (
              <div className="p-4">
                {[1, 2].map((i) => (
                  <div key={i} className="mb-3">
                    <div className="skeleton mb-2" style={{ height: "20px", width: "40%" }} />
                    <div className="skeleton mb-2" style={{ height: "14px", width: "60%" }} />
                    <div className="skeleton" style={{ height: "14px", width: "30%" }} />
                  </div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-bag-check" style={{ fontSize: "3rem" }}></i>
                <p className="mt-2">{t("activeOrder.noActiveOrders")}</p>
              </div>
            ) : (
              <div className="d-flex flex-column">
                {orders.map((order, idx) => {
                  const statusTr = order.orderStatus?.orderStatusNameTr ?? "";
                  const statusEn = order.orderStatus?.orderStatusNameEn ?? "";
                  const statusText = lang === "en" ? statusEn : statusTr;
                  const foods = order.preOrder?.foodPreOrder ?? [];
                  const restId = getRestaurantId(order);
                  const restName = restaurantNames[restId] ?? order.restourant?.restourantName ?? "";
                  const isExpanded = expandedId === order.id;

                  return (
                    <div
                      key={order.id}
                      className={idx > 0 ? "border-top" : ""}
                    >
                      {/* Order header */}
                      <div
                        className="px-3 py-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => setExpandedId(isExpanded ? null : order.id)}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                background: statusColor(statusTr),
                                flexShrink: 0,
                                animation: "pulse 2s infinite",
                              }}
                            />
                            <span className="fw-bold" style={{ color: "#c20e1a" }}>
                              {order.orderNameUniqe}
                            </span>
                          </div>
                          <span
                            className="badge rounded-pill text-white px-3 py-1"
                            style={{ background: statusColor(statusTr), fontSize: "0.78rem" }}
                          >
                            {statusText}
                          </span>
                        </div>

                        {restName && (
                          <div className="small text-muted mb-1">
                            <i className="bi bi-shop me-1"></i>{restName}
                          </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>
                            {formatDisplay(order.orderDate)}
                          </small>
                          <span className="fw-bold">
                            {order.orderPrice.toFixed(2)} {order.currency?.currencyDisplayText ?? "TL"}
                          </span>
                        </div>

                        <div className="text-center mt-1">
                          <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} text-muted small`}></i>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-3 pb-3" style={{ background: "#fafafa" }}>
                          {/* Restaurant acceptance */}
                          <div className="mb-2 pt-2">
                            <small className={order.isRestourantAccepted ? "text-success" : "text-muted"}>
                              <i className={`bi ${order.isRestourantAccepted ? "bi-check-circle-fill" : "bi-clock"} me-1`}></i>
                              {order.isRestourantAccepted
                                ? t("activeOrder.accepted")
                                : t("activeOrder.pending")}
                            </small>
                          </div>

                          {/* Delivery address */}
                          {order.customerAddress && (
                            <div className="rounded-3 p-2 mb-2 small" style={{ background: "#fff", border: "1px solid #eee" }}>
                              <i className="bi bi-geo-alt-fill text-danger me-1"></i>
                              <span className="fw-semibold">
                                {order.customerAddress.city?.cityNameTr} / {order.customerAddress.town?.townNameTr}
                              </span>
                              {order.customerAddress.customerAddressDescription && (
                                <span className="text-muted ms-1">
                                  — {order.customerAddress.customerAddressDescription}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Order note */}
                          {order.orderNote && (
                            <div className="rounded-3 p-2 mb-2 small" style={{ background: "#fff8e1", border: "1px solid #ffe082" }}>
                              <i className="bi bi-chat-left-text me-1 text-warning"></i>
                              <span className="text-muted">{order.orderNote}</span>
                            </div>
                          )}

                          {/* Cancellation reason */}
                          {order.cancellationReason && (
                            <div className="rounded-3 p-2 mb-2 small" style={{ background: "#fff4f4", border: "1px solid #f5c6c6" }}>
                              <i className="bi bi-x-circle-fill text-danger me-1"></i>
                              <span className="text-muted">{order.cancellationReason}</span>
                            </div>
                          )}

                          {/* Food items */}
                          <div className="d-flex flex-column gap-2">
                            {foods.map((fp) => (
                              <div
                                key={fp.id}
                                className="d-flex align-items-center gap-3 rounded-3 p-2"
                                style={{ background: "#fff", border: "1px solid #eee" }}
                              >
                                {fp.food?.foodImagePath && (
                                  <img
                                    src={S3_IMAGE_BASE_URL + fp.food.foodImagePath}
                                    alt={fp.food.foodNameTr}
                                    className="rounded-3"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <div className="fw-semibold small">{fp.food?.foodNameTr}</div>
                                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                    {(() => {
                                      const unit = fp.discountedUnitPrice ?? fp.unitPrice ?? fp.food?.foodPrice;
                                      const cur = order.currency?.currencyDisplayText ?? "TL";
                                      const wasDiscounted =
                                        fp.unitPrice != null &&
                                        fp.discountedUnitPrice != null &&
                                        fp.discountedUnitPrice < fp.unitPrice;
                                      return (
                                        <>
                                          {fp.foodQuantity} x{" "}
                                          {wasDiscounted && <s className="me-1">{fp.unitPrice!.toFixed(2)}</s>}
                                          {unit?.toFixed(2) ?? "?"} {cur}
                                        </>
                                      );
                                    })()}
                                  </div>
                                  {fp.foodOrderNote && (
                                    <div className="text-muted fst-italic" style={{ fontSize: "0.72rem" }}>
                                      <i className="bi bi-chat-left-text me-1"></i>{fp.foodOrderNote}
                                    </div>
                                  )}
                                  {fp.menuUpgradeNotes && (
                                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                      <i className="bi bi-plus-circle me-1"></i>{fp.menuUpgradeNotes}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Bu siparişi sohbete bağlar; asistan durum ve süreyi
                              ancak sipariş bağlıyken yanıtlayabiliyor. */}
                          <button
                            className="btn btn-sm btn-outline-danger rounded-pill w-100 mt-3"
                            data-bs-dismiss="modal"
                            onClick={() => openChat({ orderId: order.id, topic: "OrderIssue" })}
                          >
                            <i className="bi bi-headset me-1"></i>
                            {t("liveChat.helpWithOrder")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderTrackingPopup;
